import type { CSSProperties } from 'react';
import { useEditor, useEditorState, usePlayhead, formatTimecode } from '@/lib/kinema';
import AdTokenMessages from '@/shared/adTokens/AdTokenMessages';
import { adTokenSeverity } from '@/shared/adTokens/adTokenSeverity';
import {
  fixAdTokenAlias,
  useAdTokenFindings,
  type LayerAdTokenNote,
} from '../adTokenFindings';

/**
 * Every ad token finding in the composition, grouped by layer and keyframe (MPN-1933).
 *
 * Opened from the header's count. Each finding jumps to where it is -- the layer selected,
 * the playhead on its keyframe, its token selected in the Text input -- and an alias offers
 * its canonical as one undoable edit. Notes adTokenSeverity calls `todo` -- copywriter
 * instructions -- go in a group of their own.
 */

type Group = {
  layerId: string;
  name: string;
  field: LayerAdTokenNote['field'];
  keyframeTime: number | null;
  startTime: number;
  notes: LayerAdTokenNote[];
};

export function AdTokenDrawer() {
  const editor = useEditor();
  const state = useEditorState();
  const { frameRate } = usePlayhead();
  const { byLayer, tokens, drawerOpen, setDrawerOpen, jumpTo } = useAdTokenFindings();
  if (!drawerOpen) return null;

  /** One group per layer and keyframe, for the notes `which` keeps. */
  const groups = (which: (note: LayerAdTokenNote) => boolean): Group[] => {
    const out: Group[] = [];
    for (const [layerId, findings] of Object.entries(byLayer)) {
      const layer = state.layers[layerId];
      if (!layer) continue;
      for (const note of findings.notes.filter(which)) {
        const last = out[out.length - 1];
        if (last && last.layerId === layerId && last.field === note.field && last.keyframeTime === note.keyframeTime) {
          last.notes.push(note);
        } else {
          out.push({
            layerId, name: layer.name, field: note.field, keyframeTime: note.keyframeTime,
            startTime: layer.startTime, notes: [note],
          });
        }
      }
    }
    return out;
  };
  const issues = groups((note) => adTokenSeverity(note.kind) !== 'todo');
  const toFill = groups((note) => adTokenSeverity(note.kind) === 'todo');

  const renderGroups = (list: Group[]) => list.map((group) => (
    <div key={`${group.layerId}:${group.field}:${group.keyframeTime}`} style={{ padding: '6px 12px', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ fontWeight: 600, color: '#1e293b' }}>
        {group.name}
        {group.keyframeTime !== null && (
          <span style={{ fontWeight: 400, color: '#64748b' }}>
            {' '}· At {formatTimecode(group.startTime + group.keyframeTime, frameRate)}
          </span>
        )}
      </div>
      {group.notes.map((note) => (
        <div key={note.normalized} style={{ marginTop: 2 }}>
          <AdTokenMessages
            notes={[note]}
            onUseCanonical={() => fixAdTokenAlias(editor, tokens, group.layerId, note)}
          />
          {' · '}
          <button type="button" onClick={() => jumpTo(group.layerId, note)} style={linkStyle}>
            Go to
          </button>
        </div>
      ))}
    </div>
  ));

  return (
    <div
      role="region"
      aria-label="Ad token warnings"
      style={{
        position: 'absolute', top: 0, right: 0, bottom: 0, width: 320, maxWidth: '100%',
        background: '#ffffff', borderLeft: '1px solid #e2e8f0', boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
        overflowY: 'auto', zIndex: 20, fontSize: 11, fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #e2e8f0',
        fontWeight: 600, fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        <span style={{ flex: 1 }}>Ad tokens</span>
        <button
          type="button"
          onClick={() => setDrawerOpen(false)}
          aria-label="Hide ad token warnings"
          title="Hide ad token warnings"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 14, padding: 0 }}
        >
          ×
        </button>
      </div>
      {issues.length === 0 && toFill.length === 0 && (
        <div style={{ padding: '12px', color: '#64748b' }}>No ad token warnings.</div>
      )}
      {renderGroups(issues)}
      {toFill.length > 0 && (
        <>
          <div style={sectionStyle}>To fill in</div>
          {renderGroups(toFill)}
        </>
      )}
    </div>
  );
}

const linkStyle: CSSProperties = {
  border: 0, padding: 0, background: 'none', cursor: 'pointer',
  font: 'inherit', color: '#0a524d', fontWeight: 600, textDecoration: 'underline',
};

const sectionStyle: CSSProperties = {
  padding: '6px 12px', fontSize: 11, fontWeight: 600, color: '#8f5405',
  background: '#fdf3e3', borderBottom: '1px solid #f1f5f9',
};
